import {
  createStyles,
  Grid,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';

import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { colors, DefaultCard } from 'design-system';

// models
import GraphicFormatedData from 'src/models/Chart/GraphicFormatedData';
import QuotationsMeasurements from 'src/models/Chart/QuotationsMeasurements';

// icons
import { DocumentIcon } from 'src/components/Icons';

// constants
import {
  TOTAL_CLOSED_QUOTATIONS,
  TOTAL_CREATED_QUOTATIONS,
  TOTAL_EMITTED_POLICIES,
  TOTAL_OPEN_QUOTATIONS,
  TOTAL_PENDING_EMISSIONS,
  TOTAL_PENDING_INSPECTIONS,
} from 'src/constants/metricType';

// helpers
import { getImageEmptyData, isEmptyGraphicsData } from './helpers';

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    mainContainer: {
      gap: 30,
      flexWrap: 'nowrap',
      [theme.breakpoints.down('xs')]: {
        flexWrap: 'wrap',
      },
    },
    tooltip: {
      padding: '8px',
      width: 178,
      position: 'relative',
      '&::before': {
        position: 'absolute',
        left: -15,
        top: 12,
        content: '""',
        width: 0,
        height: 0,
        borderTop: '15px solid transparent',
        borderRight: '15px solid white',
        borderBottom: '15px solid transparent',
      },
    },
  }),
);

const renderColorfulLegendText = (value: string) => {
  return (
    <div
      data-testid="formatter-legend-component"
      style={{
        display: 'inline-flex',
        padding: '5px 0px',
        color: colors.madison,
        fontWeight: 600,
      }}
    >
      {value}
    </div>
  );
};

// return the correct margin icon to use inside the doughnut, when the number of digit change (1, 10, 100, 1000, 10000, 100000)
function getIconLeftMargin(totalCreatedQuotations: string): number {
  switch (totalCreatedQuotations.length) {
    case 1:
      return 35;
    case 2:
      return 45;
    case 3:
      return 55;
    case 4:
      return 65;
    case 5:
      return 75;
    case 6:
      return 85;
    default:
      return 35;
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const makeCell = (entry: any) => (
  <Cell
    key={`cell-${entry.id}`}
    fill={entry.color}
    strokeWidth={6}
    stroke="white"
  />
);

const ShamrockColor = '#43DD97';

const PieGraphics = ({
  quotationsMeasurements,
}: {
  quotationsMeasurements: QuotationsMeasurements[];
}): JSX.Element => {
  const classes = useStyle();

  // transform value in the metric response from string to number
  const arrayGraphicsData = quotationsMeasurements.map(
    ({ value, ...rest }) => ({
      ...rest,
      value: Number(value),
    }),
  );

  // transfom the array response to object where the key will be the "type" propertie and the value will be the rest of properties
  const objectGraphicsData: { [key: string]: GraphicFormatedData } =
    arrayGraphicsData.reduce(
      (accumulator, { type, ...rest }) => ({ ...accumulator, [type]: rest }),
      {},
    );

  // add name and color properties to use inside the graphics
  objectGraphicsData[TOTAL_CLOSED_QUOTATIONS] = {
    ...objectGraphicsData[TOTAL_CLOSED_QUOTATIONS],
    name: 'Cerradas',
    color: ShamrockColor,
  };
  objectGraphicsData[TOTAL_OPEN_QUOTATIONS] = {
    ...objectGraphicsData[TOTAL_OPEN_QUOTATIONS],
    name: 'Abiertas',
    color: colors.assa_blue,
  };
  objectGraphicsData[TOTAL_PENDING_EMISSIONS] = {
    ...objectGraphicsData[TOTAL_PENDING_EMISSIONS],
    name: 'Por emisión',
    color: colors.assa_blue,
  };
  objectGraphicsData[TOTAL_PENDING_INSPECTIONS] = {
    ...objectGraphicsData[TOTAL_PENDING_INSPECTIONS],
    name: 'Por inspección',
    color: colors.lightning_yellow,
  };
  objectGraphicsData[TOTAL_EMITTED_POLICIES] = {
    ...objectGraphicsData[TOTAL_EMITTED_POLICIES],
    name: 'Pólizas emitidas',
    color: ShamrockColor,
  };

  // filter data to each graphic
  const dataDoughnut = [
    objectGraphicsData[TOTAL_EMITTED_POLICIES],
    objectGraphicsData[TOTAL_PENDING_EMISSIONS],
    objectGraphicsData[TOTAL_PENDING_INSPECTIONS],
  ];
  const dataPie = [
    objectGraphicsData[TOTAL_OPEN_QUOTATIONS],
    objectGraphicsData[TOTAL_CLOSED_QUOTATIONS],
  ];

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const customLabel = ({ viewBox }: any) => {
    const textX = (viewBox?.cx as number) + 10;
    const textY = (viewBox?.cy as number) + 12;

    const totalCreatedQuotations =
      objectGraphicsData[TOTAL_CREATED_QUOTATIONS].value;
    const iconRightPadding = getIconLeftMargin(
      totalCreatedQuotations.toString(),
    );

    const iconX = textX - iconRightPadding;
    const iconY = (viewBox?.cy as number) - 12;

    return (
      <g>
        <text
          data-testid="text-center-label"
          x={textX}
          y={textY}
          textAnchor="middle"
          fontSize="36px"
          fontWeight={700}
          fill={colors.madison}
        >
          {totalCreatedQuotations}
        </text>
        <DocumentIcon x={iconX} y={iconY} data-testid="icon-center-label" />
      </g>
    );
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const customTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      const percentage =
        (100 * payload[0].value) /
        objectGraphicsData[TOTAL_CREATED_QUOTATIONS].value;
      return (
        <Paper className={classes.tooltip}>
          <Grid
            container
            style={{
              borderLeft: `2px solid ${payload[0].payload.color}`,
              fontWeight: 700,
              fontSize: '13px',
              paddingLeft: '8px',
            }}
          >
            <Grid item xs={12} data-testid="tooltip-value">
              <span style={{ color: colors.assa_blue }}>
                {payload[0].value}
              </span>{' '}
              {payload[0].name}
            </Grid>
            <Grid item xs={12} data-testid="percentage-value-tooltip">
              <span style={{ color: colors.assa_blue }}>
                {percentage.toFixed(2)}%
              </span>{' '}
              del total de ventas
            </Grid>
          </Grid>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Grid
      item
      xs={12}
      sm={12}
      md={10}
      container
      className={classes.mainContainer}
    >
      <Grid item xs={12} sm={6} md={6}>
        <DefaultCard cardStyles={{ padding: 20 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography>Estado de cotizaciones</Typography>
            </Grid>
            <Grid xs={12} item style={{ height: 296 }}>
              {!isEmptyGraphicsData(dataDoughnut) ? (
                <ResponsiveContainer width="99%" height="100%">
                  <PieChart margin={{ bottom: 0, left: 0, right: 0, top: 0 }}>
                    <Pie
                      data={dataDoughnut}
                      data-testid="quotation-status-graphics"
                      dataKey="value"
                      innerRadius="50%"
                      fill="#82ca9d"
                    >
                      <Label
                        content={customLabel}
                        data-testid="label-quotations-by-status"
                      />
                      {dataDoughnut.map(makeCell)}
                    </Pie>
                    <Tooltip
                      content={customTooltip}
                      data-testid="custom-tooltip"
                      allowEscapeViewBox={{ x: true, y: true }}
                      wrapperStyle={{ top: -28 }}
                    />
                    <Legend
                      formatter={renderColorfulLegendText}
                      data-testid="legend-status-graphics"
                      layout="vertical"
                      iconType="circle"
                      align="right"
                      verticalAlign="top"
                      wrapperStyle={{ top: 50 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                getImageEmptyData()
              )}
            </Grid>
          </Grid>
        </DefaultCard>
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <DefaultCard cardStyles={{ padding: 20 }}>
          <Grid container>
            <Grid item xs={12}>
              <Typography>Ventas cerradas vs abiertas</Typography>
            </Grid>
            <Grid item xs={12} style={{ height: 296 }}>
              {!isEmptyGraphicsData(dataPie) ? (
                <ResponsiveContainer width="99%" height="100%">
                  <PieChart margin={{ bottom: 0, left: 0, right: 0, top: 0 }}>
                    <Pie
                      data={dataPie}
                      data-testid="open-close-graphics"
                      dataKey="value"
                      fill="#82ca9d"
                    >
                      {dataPie.map(makeCell)}
                    </Pie>
                    <Tooltip
                      offset={-5}
                      allowEscapeViewBox={{ x: true, y: true }}
                      content={customTooltip}
                    />
                    <Legend
                      formatter={renderColorfulLegendText}
                      data-testid="legend-open-close-graphics"
                      layout="vertical"
                      iconType="circle"
                      align="right"
                      verticalAlign="top"
                      wrapperStyle={{ top: 50 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                getImageEmptyData()
              )}
            </Grid>
          </Grid>
        </DefaultCard>
      </Grid>
    </Grid>
  );
};

export default PieGraphics;
